import { useAsync } from 'react-use';
import { Octokit } from '@octokit/rest';
import {
  githubAuthApiRef,
  IdentityApi,
  identityApiRef,
  OAuthApi,
  useApi,
} from '@backstage/core-plugin-api';
import { useEffect } from 'react';
import { scmIntegrationsApiRef } from '@backstage/integration-react';
import { Octokit as Core } from '@octokit/core';
import { components } from '@octokit/openapi-types';
import { ScmIntegrationRegistry } from '@backstage/integration';
import { PromiseType } from 'react-use/lib/util';
import { usePersistedState } from '../util/persistedState';
import { DEFAULT_SCM_HOST } from '../util/constants';
import { fetchUrlAsObject } from '../util/fetchUrl';
// @ts-ignore
import { retry } from '@octokit/plugin-retry';
import {
  catalogApiRef,
  humanizeEntityRef,
} from '@backstage/plugin-catalog-react';
import { CatalogApi } from '@backstage/catalog-client';
import axios, { AxiosResponse } from 'axios';
import { JsonValue } from '@backstage/types';
import { GetResponseTypeFromEndpointMethod } from '@octokit/types';
import { Duration } from 'date-fns';
import { legacyRestEndpointMethods } from '@octokit/plugin-rest-endpoint-methods';
import { paginateRest } from '@octokit/plugin-paginate-rest';
import { parseOwnershipRef } from '../util/entityUtils';
import { ConfigHelper } from '@devxdiscover/devhub-common';

const octokitType = new Octokit();
type CreatePullRequestResponse = GetResponseTypeFromEndpointMethod<
  typeof octokitType.pulls.create
>;

type MaybeGitHubPrSearchResult = GitHubPrSearchResult | undefined;
type GitHubPrSearchResult = {
  total_count: number;
  incomplete_results: boolean;
  items: GitHubPr[];
};

/**
 *  A single GitHub pull request with a {@link PrStatusType}
 */
export type GitHubPr = components['schemas']['issue-search-result-item'] & {
  status: PrStatusType;
};

/**
 * The status of a pull request
 */
export enum PrStatusType {
  PendingMerge = 'Pending Merge',
  PendingReview = 'Pending Review',
  PendingApproval = 'Pending Approval',
}

/**
 * A result from a GitHub PR search.
 */
class GitHubPrResult {
  private readonly _pendingMerge: MaybeGitHubPrSearchResult;
  private readonly _pendingReview: MaybeGitHubPrSearchResult;
  private readonly _pendingApproval: MaybeGitHubPrSearchResult;

  constructor(
    pendingMerge: MaybeGitHubPrSearchResult,
    pendingReview: MaybeGitHubPrSearchResult,
    pendingApproval: MaybeGitHubPrSearchResult,
  ) {
    this._pendingMerge = pendingMerge;
    this._pendingReview = pendingReview;
    this._pendingApproval = pendingApproval;
  }

  /**
   * Gets all pull requests ordered by updated date.
   */
  get allPrs(): GitHubPr[] {
    const allPrs = [
      ...(this._pendingMerge?.items
        .filter(x => x !== undefined)
        .flatMap(x => {
          x.status = PrStatusType.PendingMerge;
          return x as GitHubPr;
        }) as GitHubPr[]),
      ...(this._pendingReview?.items
        .filter(x => x !== undefined)
        .map(x => {
          x.status = PrStatusType.PendingReview;
          return x as GitHubPr;
        }) as GitHubPr[]),
      ...(this._pendingApproval?.items
        .filter(x => x !== undefined)
        .map(x => {
          x.status = PrStatusType.PendingApproval;
          return x as GitHubPr;
        }) as GitHubPr[]),
    ];
    // Sort by the last updated date
    allPrs.sort((a, b) => {
      const aDate = new Date(a.updated_at);
      const bDate = new Date(b.updated_at);
      return bDate.getTime() - aDate.getTime();
    });
    return allPrs;
  }
}

/**
 * A hook that returns the pull requests for the current users that are pending review or merge.
 * @param maxResults - The maximum number of entries to return.
 * @param cacheDuration - The {@link Duration} after which the state should be considered expired.
 */
export const useGitHubPrRequest: (
  maxResults?: number,
  cacheDuration?: Duration,
) => {
  loading: boolean;
  error?: Error;
  value?: PromiseType<Promise<GitHubPr[]>>;
} = (maxResults: number = 10, cacheDuration: Duration = { minutes: 1 }) => {
  const auth = useApi(githubAuthApiRef);
  const identityApi = useApi(identityApiRef);
  const scmIntegrationRegistry = useApi(scmIntegrationsApiRef);

  const [foundPrs, setFoundPrs] = usePersistedState<GitHubPr[]>(
    'pr-status',
    cacheDuration,
    [],
  );

  let octokit: Awaited<ReturnType<typeof tryCreateGitHubClient>>;

  async function defaultGitHubSearchProvider(
    query: string,
  ): Promise<MaybeGitHubPrSearchResult> {
    if (!octokit) {
      octokit = await tryCreateGitHubClient(auth, scmIntegrationRegistry);
    }
    if (octokit) {
      const response = await octokit.search.issuesAndPullRequests({
        q: query,
        per_page: maxResults,
      });
      return response?.data as MaybeGitHubPrSearchResult;
    }
    return await appClientSearchProvider(query);
  }

  async function appClientSearchProvider(
    query: string,
  ): Promise<MaybeGitHubPrSearchResult> {
    const url = ConfigHelper.instance.apiUrl('github-reader/prs');
    url.searchParams.append('q', query);
    url.searchParams.append('per_page', maxResults.toString());

    const response = await fetch(url.href);
    return (await response?.json()) as MaybeGitHubPrSearchResult;
  }

  const { value, loading, error } = useAsync(async (): Promise<GitHubPr[]> => {
    const username = await getCurrentUsername(identityApi);

    if ((foundPrs?.length ?? 0) === 0) {
      const pendingReview = `is:open is:pr review-requested:${username} archived:false draft:false -review:approved`;
      const pendingReviewResults =
        await defaultGitHubSearchProvider(pendingReview);

      // Get the PRs that are pending merge
      const pendingApproval = `is:open is:pr author:${username} archived:false draft:false review:required`;
      const pendingApprovalResults =
        await defaultGitHubSearchProvider(pendingApproval);

      // Get the PRs that are pending merge
      const pendingMerge = `is:open is:pr author:${username} archived:false draft:false review:approved`;
      const pendingMergeResults =
        await defaultGitHubSearchProvider(pendingMerge);

      // Get all PRs, but no more than `maxResults`
      return new GitHubPrResult(
        pendingMergeResults,
        pendingReviewResults,
        pendingApprovalResults,
      ).allPrs.slice(0, maxResults);
    }
    return foundPrs;
  }, [maxResults]);

  useEffect(() => {
    if (value) {
      setFoundPrs(value);
    }
  }, [value, setFoundPrs]);

  return {
    value,
    loading,
    error,
  };
};

async function getCurrentUsername(identityApi: IdentityApi) {
  return (await identityApi.getBackstageIdentity()).userEntityRef.split('/')[1];
}

async function getGroups(identityApi: IdentityApi) {
  return (await identityApi.getBackstageIdentity()).ownershipEntityRefs.filter(
    ownerRef => ownerRef.startsWith('group:'),
  );
}

/**
 * React hook that returns Orgs for the authenticated user
 * @returns loading - loading boolean flag
 * @returns error - optionally returns Error object
 */
export function useOrgsByUser(): {
  loading: boolean;
  error?: Error;
  value?: string[];
} {
  const identityApi = useApi(identityApiRef);
  const { value, loading, error } = useAsync(async (): Promise<string[]> => {
    const duplGroups = await getGroups(identityApi).then(groups =>
      groups.map(grp => {
        const parsedGrp = parseOwnershipRef(grp);
        return parsedGrp?.orgName;
      }),
    );
    return [...new Set(duplGroups)];
  });
  return { value, loading, error };
}

/**
 * React hook that returns devhub groups for the authenticated user
 * @returns loading - loading boolean flag
 * @returns error - optionally returns Error object
 */
export function useGroupsByUser(): {
  loading: boolean;
  error?: Error;
  value?: string[];
} {
  const identityApi = useApi(identityApiRef);
  const { value, loading, error } = useAsync(async (): Promise<string[]> => {
    return await getGroups(identityApi).then(groups =>
      groups.map(grp => {
        const parsedGrp = parseOwnershipRef(grp);
        return `${parsedGrp?.orgName}/${parsedGrp?.group}`;
      }),
    );
  });
  return { value, loading, error };
}

async function getGroupsByHost(
  host: string,
  identityApi: IdentityApi,
  catalogApi: CatalogApi,
): Promise<string[]> {
  const identityRefs = (await identityApi.getBackstageIdentity())
    .ownershipEntityRefs;
  const groups = await catalogApi.getEntities({
    filter: {
      kind: 'Group',
      'metadata.annotations.backstage.io/managed-by-location': `github-identity-discovery-${host}:${host}`,
      ['relations.hasMember']: identityRefs || [],
    },
  });
  const entityRefs = groups?.items
    .map(e => humanizeEntityRef(e, { defaultKind: 'group' }))
    .filter(n => n);
  return entityRefs;
}

async function getOrgsByHost(
  host: string,
  identityApi: IdentityApi,
  catalogApi: CatalogApi,
): Promise<string[]> {
  const identityRefs = (await identityApi.getBackstageIdentity())
    .ownershipEntityRefs;
  const orgs = await catalogApi
    .getEntities({
      filter: {
        kind: 'Group',
        'metadata.annotations.backstage.io/managed-by-location': `github-identity-discovery-${host}:${host}`,
        ['relations.hasMember']: identityRefs || [],
      },
    })
    .then(out => out.items.map(item => item.metadata.namespace!));
  return orgs;
}

/**
 * React hook that returns Groups for authenticated user and for all the SCM hosts
 * @param hosts - list of allowed scm hosts ex. ['github.comcast.com','github.com']
 * @returns loading - loading boolean flag
 * @returns error - optionally returns Error object
 */
export function useGroupsByLocation(hosts: string[]): {
  loading: boolean;
  error?: Error;
  value?: Map<string, string[]>;
} {
  const data = new Map<string, string[]>();
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { value, loading, error } = useAsync(
    async (): Promise<Map<string, string[]>> => {
      for (const host of hosts) {
        const duplGroups = await getGroupsByHost(host, identityApi, catalogApi);
        data.set(host, [...new Set(duplGroups)]);
      }
      return data;
    },
  );
  return { value, loading, error };
}

/**
 * React hook that returns Orgs for authenticated user and for all the SCM hosts
 * @param hosts - list of allowed scm hosts ex. ['github.comcast.com','github.com']
 * @returns loading - loading boolean flag
 * @returns error - optionally returns Error object
 */
export function useOrgsByLocation(hosts: string[]): {
  loading: boolean;
  error?: Error;
  value?: Map<string, string[]>;
} {
  const data = new Map<string, string[]>();
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { value, loading, error } = useAsync(
    async (): Promise<Map<string, string[]>> => {
      for (const host of hosts) {
        const duplOrgs = await getOrgsByHost(host, identityApi, catalogApi);
        data.set(host, [...new Set(duplOrgs)]);
      }
      return data;
    },
  );
  return { value, loading, error };
}

/**
 * Gets the authenticated GitHub client
 * @param auth - Authenticated OAuth type with token
 * @param scmIntegrationRegistry - GitHub config
 */
export async function tryCreateGitHubClient(
  auth: OAuthApi,
  scmIntegrationRegistry: ScmIntegrationRegistry,
) {
  const gitHub = scmIntegrationRegistry.github.byHost(DEFAULT_SCM_HOST);
  const baseUrl = gitHub?.config?.apiBaseUrl;
  const token =
    gitHub?.config?.token ??
    (await auth.getAccessToken(undefined, { optional: true }));
  if (token) {
    const RetryableOctokit = Core.plugin(
      retry,
      // @ts-ignore
      legacyRestEndpointMethods,
      paginateRest,
    );
    return new RetryableOctokit({
      auth: token,
      baseUrl: baseUrl,
      retry: {
        doNotRetry: [400, 401, 403, 404, 422, 409],
      },
    });
  }
  return undefined;
}

/**
 * Gets the list of repositories which are accessible to the authenticated user
 * and for the specified org
 * @param orgName - list repos for this org
 * @param host - scm_host ex. github.comcast.com or github.com
 */
export async function getRepos(
  orgName: string,
  host: string,
): Promise<string[]> {
  const url = ConfigHelper.instance.apiUrl('github-reader/listReposByOrg');
  url.searchParams.append('q', orgName);
  url.searchParams.append('scm_host', host);
  const fetchResult = await fetchUrlAsObject<string[]>(url.href);
  return fetchResult.result ?? [];
}

/**
 * Creates a pull request
 * @param owner - owner of the repo
 * @param title - title of the PR
 * @param body - description
 * @param repo - repo name
 * @param fileName - file name
 * @param content - content
 * @param branch - name of the branch to create
 * @param contentType - content type of request
 * @param targetFilePath - optional
 * @returns ResponseStatus - status of the response
 */
export async function createPullRequest(
  owner: string,
  title: string,
  body: string,
  repo: string,
  fileName: string,
  content: JsonValue,
  branch: string,
  contentType?: string,
  targetFilePath?: string,
): Promise<AxiosResponse> {
  const url = ConfigHelper.instance.apiUrl('github-writer/createPullRequest');
  const response = await axios.post<CreatePullRequestResponse>(url.href, {
    owner: owner,
    repo: repo,
    title: title,
    description: body,
    fileName: fileName,
    content: content,
    head: branch,
    targetFilePath: targetFilePath,
    contentType: contentType,
    update: true,
  });

  return response;
}
